import { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../api/client';
import { toast } from 'sonner';
import {
  ArrowLeft, Save, Rocket, Eye, Bot, X, Loader2,
  FileCode, ChevronRight, ChevronDown, File, Image, FileText,
  FolderOpen, Play, ExternalLink, AlertTriangle, CheckCircle,
  Info, Wand2, PanelLeft, Copy, Terminal, GripVertical
} from 'lucide-react';
import Editor from '@monaco-editor/react';
import { motion, AnimatePresence } from 'framer-motion';

interface ProjectFile { name: string; size: number; type: string; uploaded: string; }
interface FileNode { name: string; isFolder: boolean; children?: FileNode[]; fullPath: string; }
interface ScanIssue { file: string; line: number; severity: string; message: string; fix: string; }
interface CodeBlockProps { code: string; language: string; }

export default function FullEditor() {
  const { id } = useParams<{ id: string }>();
  const [project, setProject] = useState<any>(null);
  const [files, setFiles] = useState<ProjectFile[]>([]);
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [fileContent, setFileContent] = useState('');
  const [originalContent, setOriginalContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [deploying, setDeploying] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showSidebar, setShowSidebar] = useState(true);
  const [showAI, setShowAI] = useState(false);
  const [aiMessage, setAiMessage] = useState('');
  const [aiChat, setAiChat] = useState<{ role: string; content: string }[]>([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [previewKey, setPreviewKey] = useState(0);
  const [consoleWidth, setConsoleWidth] = useState(340);
  const [showConsole, setShowConsole] = useState(false);
  const [deployLogs, setDeployLogs] = useState<{ time: string; message: string; type: string }[]>([]);
  const [deployDone, setDeployDone] = useState(false);
  const [deployedUrl, setDeployedUrl] = useState('');

  // Scanner
  const [scanStatus, setScanStatus] = useState<'idle' | 'scanning' | 'complete'>('idle');
  const [scanProgress, setScanProgress] = useState(0);
  const [scanCurrent, setScanCurrent] = useState('');
  const [scanIssues, setScanIssues] = useState<ScanIssue[]>([]);
  const [scanFixed, setScanFixed] = useState<Set<number>>(new Set());

  const resizeRef = useRef<{ startX: number; startWidth: number } | null>(null);

  // ==============================
  // Load highlight.js for syntax highlighting
  // ==============================
  useEffect(() => {
    if (typeof window !== 'undefined' && !(window as any).hljs) {
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/highlight.js/11.9.0/highlight.min.js';
      script.async = true;
      script.onload = () => {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://cdnjs.cloudflare.com/ajax/libs/highlight.js/11.9.0/styles/atom-one-dark.min.css';
        document.head.appendChild(link);
        
        // Highlight any existing code blocks
        if ((window as any).hljs && typeof (window as any).hljs.highlightAll === 'function') {
          (window as any).hljs.highlightAll();
        }
      };
      document.head.appendChild(script);
    }
  }, []);

  // Highlight code when AI response updates
  useEffect(() => {
    setTimeout(() => {
      if ((window as any).hljs && typeof (window as any).hljs.highlightAll === 'function') {
        (window as any).hljs.highlightAll();
      }
    }, 100);
  }, [aiChat]);

  useEffect(() => { if (id) loadAll(); }, [id]);

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if ((e.ctrlKey||e.metaKey) && e.key==='s') { e.preventDefault(); if (dirty) saveFile(); }
      if ((e.ctrlKey||e.metaKey) && e.key==='d') { e.preventDefault(); handleDeploy(); }
      if ((e.ctrlKey||e.metaKey) && e.key==='p') { e.preventDefault(); setShowPreview(!showPreview); }
      if ((e.ctrlKey||e.metaKey) && e.key==='b') { e.preventDefault(); setShowSidebar(!showSidebar); }
    };
    window.addEventListener('keydown', h); return () => window.removeEventListener('keydown', h);
  }, [dirty, selectedFile, fileContent, showPreview, showSidebar]);

  useEffect(() => {
    const mm = (e: MouseEvent) => { if (!resizeRef.current) return; const d = resizeRef.current.startX - e.clientX; setConsoleWidth(Math.min(700, Math.max(260, resizeRef.current.startWidth + d))); };
    const mu = () => { resizeRef.current = null; };
    window.addEventListener('mousemove', mm); window.addEventListener('mouseup', mu);
    return () => { window.removeEventListener('mousemove', mm); window.removeEventListener('mouseup', mu); };
  }, []);

  async function loadAll() {
    try {
      const [proj, fileData] = await Promise.all([api.getProject(id!), api.getFiles(id!)]);
      setProject(proj.project); setFiles(fileData.files||[]);
    } catch (e: any) { toast.error(e.message); } finally { setLoading(false); }
  }

  async function openFile(name: string) {
    if (dirty && !confirm('Unsaved changes. Discard?')) return;
    try { const c = await api.getFileContent(id!, name); setSelectedFile(name); setFileContent(c); setOriginalContent(c); setDirty(false); }
    catch (e: any) { toast.error(e.message); }
  }

  async function saveFile() {
    if (!selectedFile) return; setSaving(true);
    try { await api.updateFile(id!, selectedFile, fileContent); setOriginalContent(fileContent); setDirty(false); toast.success('Saved'); }
    catch (e: any) { toast.error(e.message); } finally { setSaving(false); }
  }

  async function handleDeploy() {
    if (files.length===0) { toast.error('No files'); return; }
    if (dirty && selectedFile) await saveFile();
    setDeploying(true); setShowConsole(true); setDeployLogs([]); setDeployDone(false);
    const add = (m:string, t:string='info') => { setDeployLogs(p=>[...p,{time:new Date().toLocaleTimeString('en-US',{hour12:false}),message:m,type:t}]); };
    add('▲ 𝐀𝐏𝐄𝐗𝐃𝐄𝐕 𝐃𝐄𝐏𝐋𝐎𝐘', 'highlight'); await sleep(400);
    add(`✦ Approved and supported by 𝐀𝐱𝐢𝐬 𝐋𝐚𝐛 (axislabs.dpdns.org)`, 'highlight'); await sleep(300);
    add(`▪ ${files.length} file(s) to deploy`, 'info'); await sleep(300);
    add('▪ Copying to live environment...', 'info'); await sleep(800);
    try {
      const data = await api.deploy(id!);
      add('✔ Deployment successful', 'success'); await sleep(300);
      const url = data.deployment?.url || `https://${project.subdomain}.meganhost.dpdns.org`;
      setDeployedUrl(url); add(`✦ Live at: ${url}`, 'highlight');
      setDeployDone(true); setPreviewKey(k=>k+1); loadAll();
    } catch (e: any) { add(`✘ ${e.message}`, 'error'); } finally { setDeploying(false); }
  }

  async function sendAiMessage() {
    if (!aiMessage.trim()) return;
    const msg = aiMessage.trim();
    setAiChat(p => [...p, { role: 'user', content: msg }]);
    setAiMessage(''); setAiLoading(true);
    try {
      const context = selectedFile ? `Current file: ${selectedFile}\n${fileContent.slice(0, 2000)}` : '';
      const data = await api.aiChat(msg, context);
      setAiChat(p => [...p, { role: 'assistant', content: data.reply }]);
    } catch (e: any) { toast.error(e.message); } finally { setAiLoading(false); }
  }

  async function startScan() {
    setScanStatus('scanning'); setScanProgress(0); setScanIssues([]); setScanFixed(new Set());
    const fileData: { name: string; content: string }[] = [];
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      setScanCurrent(f.name); setScanProgress(Math.floor((i / files.length) * 60));
      try { const content = await api.getFileContent(id!, f.name); fileData.push({ name: f.name, content }); await sleep(200); }
      catch { /* skip */ }
    }
    setScanProgress(70); setScanCurrent('Analyzing with Megan AI...'); await sleep(600);
    try {
      const data = await api.scanFiles(fileData.slice(0, 20));
      setScanProgress(100); setScanStatus('complete'); setScanIssues(data.issues || []);
    } catch (e: any) { setScanStatus('idle'); toast.error('Scan failed: ' + e.message); }
  }

  async function applyFix(index: number) {
    const issue = scanIssues[index]; if (!issue) return;
    try {
      const content = await api.getFileContent(id!, issue.file);
      const lines = content.split('\n');
      if (issue.line > 0 && issue.line <= lines.length) {
        lines[issue.line - 1] = issue.fix;
        const newContent = lines.join('\n');
        await api.updateFile(id!, issue.file, newContent);
        if (selectedFile === issue.file) { setFileContent(newContent); setOriginalContent(newContent); setDirty(false); }
        setScanFixed(p => new Set([...p, index])); toast.success(`Fixed ${issue.file}:${issue.line}`);
      }
    } catch (e: any) { toast.error(e.message); }
  }

  function getFileIcon(name: string) {
    const ext = name.split('.').pop()?.toLowerCase();
    if (name.includes('/')) return <File size={13} className="text-accent-blue shrink-0" />;
    if (['png','jpg','jpeg','gif','svg','webp','ico'].includes(ext||'')) return <Image size={13} className="text-accent-blue shrink-0" />;
    if (['html','htm'].includes(ext||'')) return <FileCode size={13} className="text-warning shrink-0" />;
    if (['css','scss','less'].includes(ext||'')) return <FileCode size={13} className="text-accent-blue shrink-0" />;
    if (['js','ts','jsx','tsx','mjs'].includes(ext||'')) return <FileCode size={13} className="text-warning shrink-0" />;
    return <File size={13} className="text-text-muted shrink-0" />;
  }

  function getLanguage(name: string): string {
    const ext = name.split('.').pop()?.toLowerCase();
    const m: Record<string,string> = { html:'html', htm:'html', css:'css', js:'javascript', jsx:'javascript', ts:'typescript', tsx:'typescript', json:'json' };
    return m[ext||''] || 'plaintext';
  }

  const fileTree = useMemo(() => buildTree(files), [files]);

  if (loading) return <div className="h-screen flex items-center justify-center bg-bg"><Loader2 className="animate-spin text-accent" size={24} /></div>;
  if (!project) return <div className="h-screen flex items-center justify-center bg-bg"><h2>Not found</h2><Link to="/dashboard" className="text-accent">Back</Link></div>;

  const projectUrl = `https://${project.subdomain}.meganhost.dpdns.org`;

  return (
    <div className="h-screen flex flex-col bg-bg">
      {/* Header */}
      <header className="glass border-b border-border h-11 flex items-center px-3 justify-between shrink-0 z-20">
        <div className="flex items-center gap-2 min-w-0">
          <Link to={`/project/${id}`} className="p-1.5 rounded-lg hover:bg-elevated text-text-muted hover:text-text-primary shrink-0">
            <ArrowLeft size={15} />
          </Link>
          <Link to="/dashboard" className="flex items-center gap-2 shrink-0 group">
            <img src="/logo.svg" alt="ApexDev" className="w-5 h-5" />
            <span className="font-bold text-sm bg-gradient-to-r from-accent to-accent-blue bg-clip-text text-transparent">
              ApexDev
            </span>
          </Link>
          <span className="text-text-muted text-sm">/</span>
          <Link to={`/project/${id}`} className="text-xs text-text-muted hover:text-text-primary truncate transition-colors">
            {project?.name || 'Project'}
          </Link>
          <span className="text-text-muted text-sm">/</span>
          <span className="text-xs text-text-primary truncate font-medium">Editor</span>
          {selectedFile && (
            <>
              <span className="text-text-muted text-sm">/</span>
              <span className="text-[10px] text-text-muted truncate">{selectedFile}</span>
            </>
          )}
          {dirty && <span className="text-[10px] text-warning font-medium ml-1">• Unsaved</span>}
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <span className="text-[9px] text-text-muted hidden xl:inline">Ctrl+S Save · Ctrl+D Deploy · Ctrl+P Preview</span>
          <div className="w-px h-4 bg-border hidden xl:block" />
          <button onClick={() => setShowSidebar(!showSidebar)} className={`p-1.5 rounded text-text-muted transition-colors ${showSidebar ? 'bg-elevated text-accent' : 'hover:bg-elevated'}`} title="Toggle Sidebar (Ctrl+B)">
            <PanelLeft size={14} />
          </button>
          <button onClick={startScan} disabled={scanStatus==='scanning'} className="btn-secondary text-[11px] py-1 px-2 flex items-center gap-1">
            {scanStatus==='scanning' ? <Loader2 size={11} className="animate-spin" /> : <Wand2 size={11} />}
            <span className="hidden sm:inline">Scan</span>
          </button>
          <button onClick={() => setShowAI(!showAI)} className={`p-1.5 rounded text-text-muted transition-colors ${showAI ? 'bg-elevated text-accent' : 'hover:bg-elevated'}`} title="AI Assistant">
            <Bot size={14} />
          </button>
          <div className="w-px h-4 bg-border" />
          {dirty && (
            <button onClick={saveFile} disabled={saving} className="btn-primary text-[11px] py-1 px-2 flex items-center gap-1">
              {saving ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />}
              Save
            </button>
          )}
          <button onClick={() => setShowPreview(!showPreview)} className={`p-1.5 rounded text-text-muted transition-colors ${showPreview ? 'bg-elevated text-accent' : 'hover:bg-elevated'}`} title="Toggle Preview (Ctrl+P)">
            <Eye size={14} />
          </button>
          <button onClick={handleDeploy} disabled={deploying} className="btn-primary text-[11px] py-1 px-2 flex items-center gap-1">
            {deploying ? <Loader2 size={11} className="animate-spin" /> : <Rocket size={11} />}
            Deploy
          </button>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <AnimatePresence>
          {showSidebar && (
            <motion.div initial={{ width: 0, opacity: 0 }} animate={{ width: 180, opacity: 1 }} exit={{ width: 0, opacity: 0 }} transition={{ duration: 0.15 }}
              className="border-r border-border bg-surface/30 overflow-y-auto shrink-0">
              <div className="p-2 border-b border-border sticky top-0 bg-surface/50 backdrop-blur">
                <span className="text-[10px] font-semibold text-text-muted uppercase tracking-wider">Files</span>
              </div>
              <div className="py-1">
                {renderTree(fileTree, 0, expandedFolders, (p) => setExpandedFolders(prev => { const n = new Set(prev); n.has(p) ? n.delete(p) : n.add(p); return n; }), openFile, selectedFile, getFileIcon)}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className={`flex-1 flex ${showPreview ? 'flex-col md:flex-row' : ''} overflow-hidden`}>
            {/* Editor */}
            <div className={`${showPreview ? 'flex-1' : 'flex-1'} flex flex-col overflow-hidden`}>
              {selectedFile ? (
                <div className="flex-1">
                  <Editor height="100%" language={getLanguage(selectedFile)} value={fileContent}
                    onChange={v => { setFileContent(v||''); setDirty((v||'') !== originalContent); }} theme="vs-dark"
                    options={{ minimap:{enabled:false}, fontSize:13, fontFamily:'Geist Mono, JetBrains Mono, monospace',
                      padding:{top:12}, scrollBeyondLastLine:false, automaticLayout:true, wordWrap:'on',
                      formatOnPaste:true, tabSize:2, smoothScrolling:true, cursorBlinking:'smooth',
                      stickyScroll:{enabled:true}, bracketPairColorization:{enabled:true}, renderWhitespace:'selection' }} />
                </div>
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <FileCode size={48} className="mx-auto mb-4 text-text-muted opacity-50" />
                    <h2 className="font-semibold text-lg mb-1">Welcome to the Editor</h2>
                    <p className="text-text-muted text-sm mb-4">Select a file from the sidebar to start editing</p>
                    <div className="flex gap-2 justify-center text-xs text-text-muted">
                      <span className="flex items-center gap-1"><span className="px-1.5 py-0.5 bg-elevated rounded text-[10px]">Ctrl+S</span> Save</span>
                      <span className="flex items-center gap-1"><span className="px-1.5 py-0.5 bg-elevated rounded text-[10px]">Ctrl+D</span> Deploy</span>
                      <span className="flex items-center gap-1"><span className="px-1.5 py-0.5 bg-elevated rounded text-[10px]">Ctrl+P</span> Preview</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Preview panel */}
            <AnimatePresence>
              {showPreview && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }}
                  className="md:w-[45%] border-t md:border-t-0 md:border-l border-border overflow-hidden flex flex-col">
                  <div className="border-b border-border px-3 py-1.5 flex items-center justify-between bg-surface/50 shrink-0">
                    <span className="text-[10px] text-text-muted flex items-center gap-1.5"><Eye size={11} /> Preview</span>
                    <div className="flex items-center gap-1">
                      <button onClick={() => setPreviewKey(k=>k+1)} className="text-[10px] text-text-muted hover:text-text-primary px-1.5 py-0.5 rounded hover:bg-elevated">Refresh</button>
                      <a href={projectUrl} target="_blank" rel="noopener noreferrer" className="text-[10px] text-accent flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-elevated"><ExternalLink size={10} /> Open</a>
                    </div>
                  </div>
                  <div className="flex-1 bg-white">
                    <iframe key={previewKey} src={projectUrl} className="w-full h-full border-0" title="Preview" sandbox="allow-scripts allow-same-origin" />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* AI Panel */}
        <AnimatePresence>
          {showAI && (
            <motion.div initial={{ width: 0, opacity: 0 }} animate={{ width: 320, opacity: 1 }} exit={{ width: 0, opacity: 0 }} transition={{ duration: 0.15 }}
              className="border-l border-border bg-surface/50 overflow-hidden flex flex-col shrink-0">
              <div className="border-b border-border px-3 py-2 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-1.5"><Bot size={13} className="text-accent-purple" /><span className="text-[11px] font-semibold">Megan AI</span></div>
                <button onClick={() => setShowAI(false)} className="p-1 rounded hover:bg-elevated"><X size={13} /></button>
              </div>
              <div className="flex-1 overflow-y-auto p-3 space-y-3">
                {/* Scanner results */}
                {scanStatus === 'scanning' && (
                  <div className="card py-4">
                    <div className="text-center">
                      <motion.div animate={{ scale: [1, 1.1, 1] }} transition={{ repeat: Infinity, duration: 1.5 }}>
                        <Wand2 size={24} className="mx-auto mb-2 text-accent-purple" />
                      </motion.div>
                      <p className="text-xs font-semibold mb-1">Scanning files...</p>
                      <p className="text-[10px] text-text-muted mb-3 truncate">{scanCurrent || 'Starting...'}</p>
                      <div className="bg-elevated rounded-full h-1.5 overflow-hidden">
                        <motion.div className="h-full bg-accent-purple rounded-full" initial={{ width: 0 }} animate={{ width: `${scanProgress}%` }} transition={{ duration: 0.3 }} />
                      </div>
                      <p className="text-[10px] text-text-muted mt-1">{scanProgress}%</p>
                    </div>
                  </div>
                )}

                {scanStatus === 'complete' && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-[11px] font-semibold">
                        {scanIssues.length === 0 ? 'No issues found!' : `${scanIssues.length} issue${scanIssues.length>1?'s':''} found`}
                      </p>
                      {scanIssues.length > 0 && (
                        <span className="text-[10px] text-text-muted">Click "View File" to fix manually</span>
                      )}
                    </div>
                    {scanIssues.length === 0 && (
                      <div className="text-center py-4">
                        <CheckCircle size={24} className="mx-auto mb-2 text-accent" />
                        <p className="text-xs text-text-muted">Your code looks great!</p>
                      </div>
                    )}
                    {scanIssues.map((issue, i) => (
                      <div key={i} className={`card py-2 px-3 mb-1.5 transition-opacity ${scanFixed.has(i) ? 'opacity-50' : ''}`}>
                        <div className="flex items-start gap-2">
                          {issue.severity === 'error' ? <AlertTriangle size={13} className="text-danger shrink-0 mt-0.5" /> :
                           issue.severity === 'warning' ? <AlertTriangle size={13} className="text-warning shrink-0 mt-0.5" /> :
                           <Info size={13} className="text-accent-blue shrink-0 mt-0.5" />}
                          <div className="flex-1 min-w-0">
                            <p className="text-[11px] font-medium font-mono">{issue.file}:{issue.line}</p>
                            <p className="text-[10px] text-text-muted mt-0.5">{issue.message}</p>
                          </div>
                        </div>
                        <button 
                            onClick={() => {
                              // Open the file in editor so user can fix manually
                              openFile(issue.file);
                              // Optionally show the issue message
                              toast.info(`${issue.message}`, { duration: 5000 });
                            }} 
                            className="text-[10px] text-accent-blue hover:underline mt-1.5 ml-5"
                          >
                            View File
                          </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Axis Lab Badge */}
                {aiChat.length > 0 && (
                  <div className="border-t border-border pt-2 mt-2">
                    <a href="https://axislabs.dpdns.org" target="_blank" rel="noopener noreferrer" className="axis-badge justify-center w-full">
                      <span>✦</span>
                      <span className="text-[10px]">Axis Lab</span>
                    </a>
                  </div>
                )}

                {/* Chat history */}
                {aiChat.length > 0 && <div className="border-t border-border pt-2" />}
                {aiChat.map((msg, i) => (
                  <div key={i} className={`text-[11px] ${msg.role === 'user' ? 'text-accent-blue' : 'text-text-primary'}`}>
                    <p className="text-[10px] text-text-muted mb-0.5 font-medium">{msg.role === 'user' ? 'You' : 'Megan AI'}</p>
                    {msg.role === 'assistant' ? (
                      <AICodeRenderer content={msg.content} />
                    ) : (
                      <div className="whitespace-pre-wrap leading-relaxed text-text-primary">{msg.content}</div>
                    )}
                  </div>
                ))}
                {aiLoading && (
                  <div className="flex items-center gap-2 text-text-muted text-[11px]">
                    <Loader2 size={12} className="animate-spin" /> Thinking...
                  </div>
                )}
              </div>
              <div className="p-2 border-t border-border shrink-0">
                <div className="flex gap-1">
                  <input value={aiMessage} onChange={e => setAiMessage(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendAiMessage(); } }}
                    placeholder="Ask AI or click Scan above..."
                    className="input text-xs py-1.5 flex-1" />
                  <button onClick={sendAiMessage} disabled={aiLoading || !aiMessage.trim()} className="btn-primary text-xs px-3"><Play size={12} /></button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Console panel */}
        <AnimatePresence>
          {showConsole && (
            <>
              <div className="w-1 cursor-col-resize hover:bg-accent/30 shrink-0 flex items-center justify-center group"
                onMouseDown={e => { resizeRef.current = { startX: e.clientX, startWidth: consoleWidth }; }}>
                <GripVertical size={12} className="text-text-muted opacity-0 group-hover:opacity-100" />
              </div>
              <motion.div initial={{ width: 0, opacity: 0 }} animate={{ width: consoleWidth, opacity: 1 }} exit={{ width: 0, opacity: 0 }} transition={{ duration: 0.15 }}
                className="border-l border-border bg-[#0A0A0A] flex flex-col shrink-0 overflow-hidden">
                <div className="border-b border-border px-3 py-2 flex items-center justify-between shrink-0">
                  <div className="flex items-center gap-2"><Terminal size={13} className="text-accent" /><span className="text-[11px] font-semibold">Deploy Console</span></div>
                  <button onClick={() => { setShowConsole(false); setDeployDone(false); }} className="p-1 rounded hover:bg-elevated"><X size={13} /></button>
                </div>
                <div className="flex-1 overflow-y-auto p-3 font-mono text-[11px] space-y-0.5">
                  {deployLogs.map((log, i) => (
                    <div key={i} className={`flex gap-2 ${log.type==='error'?'text-danger':log.type==='success'?'text-accent':log.type==='highlight'?'text-accent-blue font-bold':'text-text-muted'}`}>
                      <span className="text-text-muted shrink-0">{log.time}</span>
                      <span>{log.message}</span>
                    </div>
                  ))}
                  {deploying && <div className="flex gap-2 text-text-muted"><Loader2 size={12} className="animate-spin" /></div>}
                </div>
                {deployDone && deployedUrl && (
                  <div className="p-3 border-t border-border space-y-2">
                    <button onClick={() => window.open(deployedUrl, '_blank')} className="btn-primary w-full text-xs py-2 flex items-center justify-center gap-1.5"><ExternalLink size={12} /> Open Site</button>
                    <button onClick={() => { navigator.clipboard.writeText(deployedUrl); toast.success('Copied!'); }} className="btn-secondary w-full text-xs py-2 flex items-center justify-center gap-1.5"><Copy size={12} /> Copy URL</button>
                  </div>
                )}
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>

      {/* Status bar */}
      <div className="glass border-t border-border h-6 flex items-center px-3 justify-between text-[10px] text-text-muted shrink-0 z-10">
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1"><span className={`w-1.5 h-1.5 rounded-full ${project.status==='deployed'?'bg-accent':'bg-warning'}`} /> {project.status}</span>
          <span>{files.length} files</span>
          {selectedFile && <span>{getLanguage(selectedFile)}</span>}
        </div>
        <div className="flex items-center gap-3">
          {dirty && <span className="text-warning">Unsaved changes</span>}
          <Link to={`/project/${id}/settings`} className="hover:text-text-primary">Settings</Link>
          <span>ApexDev</span>
        </div>
      </div>
    </div>
  );
}

// ==============================
// CODE BLOCK COMPONENT
// ==============================
function highlightCode(code: string, lang: string): string {
  if (typeof window !== 'undefined' && (window as any).hljs) {
    const hljs = (window as any).hljs;
    if (hljs.getLanguage(lang)) {
      try {
        const result = hljs.highlight(code, { language: lang });
        return result.value;
      } catch {}
    }
    try {
      const result = hljs.highlightAuto(code);
      return result.value;
    } catch {}
  }
  return code.replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function CodeBlock({ code, language }: CodeBlockProps) {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = () => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="my-3 rounded-xl overflow-hidden border border-border/60 bg-[#0A0A0A]"
      style={{
        boxShadow: '0 0 20px rgba(34, 197, 94, 0.08), 0 0 40px rgba(59, 130, 246, 0.04), inset 0 0 20px rgba(34, 197, 94, 0.02)'
      }}>
      <div className="flex items-center justify-between px-4 py-2.5 bg-gradient-to-r from-surface/70 to-surface/30 border-b border-border/40 backdrop-blur-sm">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-accent/60" />
          <span className="text-xs font-mono text-text-muted font-semibold uppercase tracking-wider">
            {language || 'code'}
          </span>
        </div>
        <button
          onClick={handleCopy}
          className={`text-xs px-3 py-1 rounded-md font-medium transition-all duration-200 flex items-center gap-1.5 ${
            copied
              ? 'bg-accent/20 text-accent'
              : 'bg-elevated hover:bg-elevated/80 text-text-muted hover:text-text-primary'
          }`}
        >
          {copied ? (
            <>
              <CheckCircle size={12} />
              Copied!
            </>
          ) : (
            <>
              <Copy size={12} />
              Copy
            </>
          )}
        </button>
      </div>
      <pre className="p-4 m-0 overflow-x-auto max-h-96">
        <code 
          className={`language-${language || 'plaintext'} text-sm font-mono leading-relaxed`}
          dangerouslySetInnerHTML={{ __html: highlightCode(code, language || 'plaintext') }}
        />
      </pre>
    </div>
  );
}

// ==============================
// AI CODE RENDERER COMPONENT
// ==============================
function AICodeRenderer({ content }: { content: string }) {
  const codeBlockRegex = /```(\w*)\n([\s\S]*?)```/g;
  const parts: Array<{ type: 'text' | 'code'; content: string; language?: string }> = [];
  
  let lastIndex = 0;
  let match;
  
  while ((match = codeBlockRegex.exec(content)) !== null) {
    if (match.index > lastIndex) {
      parts.push({
        type: 'text',
        content: content.substring(lastIndex, match.index)
      });
    }
    parts.push({
      type: 'code',
      language: match[1] || 'plaintext',
      content: match[2]
    });
    lastIndex = codeBlockRegex.lastIndex;
  }
  
  if (lastIndex < content.length) {
    parts.push({
      type: 'text',
      content: content.substring(lastIndex)
    });
  }

  return (
    <div className="space-y-2">
      {parts.map((part, idx) => (
        part.type === 'code' ? (
          <CodeBlock key={idx} code={part.content} language={part.language || 'plaintext'} />
        ) : (
          <div key={idx} className="whitespace-pre-wrap leading-relaxed text-text-primary">
            {part.content.split('\n').map((line, i) => (
              <div key={i}>
                {line.split(/(\*\*.*?\*\*|`[^`]+`)/g).map((segment, j) => {
                  if (segment.startsWith('**') && segment.endsWith('**')) {
                    return <strong key={j} className="text-text-primary font-semibold">{segment.slice(2, -2)}</strong>;
                  }
                  if (segment.startsWith('`') && segment.endsWith('`')) {
                    return <code key={j} className="bg-elevated px-1.5 py-0.5 rounded text-xs text-accent font-mono">{segment.slice(1, -1)}</code>;
                  }
                  return <span key={j}>{segment}</span>;
                })}
              </div>
            ))}
          </div>
        )
      ))}
    </div>
  );
}

// ==============================
// FILE TREE HELPERS
// ==============================
function buildTree(files: ProjectFile[]): FileNode[] {
  const root: FileNode[] = []; const fm = new Map<string, FileNode>();
  for (const f of files) {
    const parts = f.name.split('/'); let cur = root; let cp = '';
    for (let i=0;i<parts.length;i++) {
      const p = parts[i]; cp = cp?`${cp}/${p}`:p; const last = i===parts.length-1;
      if (last) cur.push({name:p,isFolder:false,fullPath:f.name});
      else { let folder= fm.get(cp); if(!folder) { folder={name:p,isFolder:true,children:[],fullPath:cp}; fm.set(cp,folder); cur.push(folder); } cur = folder.children!; }
    }
  }
  const srt=(n:FileNode[])=>{n.sort((a,b)=>a.isFolder!==b.isFolder?(a.isFolder?-1:1):a.name.localeCompare(b.name));for(const x of n) if(x.children) srt(x.children);};
  srt(root); return root;
}

function renderTree(nodes: FileNode[], depth: number, expanded: Set<string>, toggle: (p:string)=>void, onSelect:(n:string)=>void, selected:string|null, getIcon:(n:string)=>JSX.Element): JSX.Element[] {
  const els: JSX.Element[]=[];
  for (const n of nodes) {
    if (n.isFolder) {
      const ex = expanded.has(n.fullPath);
      els.push(<div key={n.fullPath} className="px-2 py-0.5 text-[11px] text-text-muted flex items-center gap-1 cursor-pointer hover:bg-elevated transition-colors" style={{paddingLeft:8+depth*10}} onClick={()=>toggle(n.fullPath)}>{ex?<ChevronDown size={10}/>:<ChevronRight size={10}/>}<File size={11} className="text-accent-blue shrink-0"/><span className="truncate">{n.name}</span></div>);
      if (ex && n.children) els.push(...renderTree(n.children,depth+1,expanded,toggle,onSelect,selected,getIcon));
    } else {
      els.push(<div key={n.fullPath} onClick={()=>onSelect(n.fullPath)} className={`w-full text-left px-2 py-1 text-[11px] flex items-center gap-1.5 hover:bg-elevated cursor-pointer transition-colors ${selected===n.fullPath?'bg-elevated text-accent border-l-2 border-l-accent':'text-text-primary border-l-2 border-l-transparent'}`} style={{paddingLeft:8+depth*10}}>{getIcon(n.name)}<span className="truncate">{n.name}</span></div>);
    }
  }
  return els;
}

function sleep(ms: number) { return new Promise(r=>setTimeout(r,ms)); }
